import { uniq } from "@-/com.npmjs.array-utils-ts";
import { Pressable, ScrollView, View, type ViewProps } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { SurfaceHeader, SurfaceContent } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { ReferenceWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { PinyinPronunciation } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-pronunciation";
import { MorphemeInfoPanelDictionaryRankSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.rank-section";
import {
  MorphemeInfoPanelDictionaryGradeBadge,
  useMorphemeInfoPanelDictionaryGradeLabel,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.grade-badge";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import { useMemo } from "react";

/** Compact word header, with pronunciation, definitions and links to its characters. */
export function WordDictionarySurfaceHeader({
  compact = false,
  onLayout,
  wordCard,
}: {
  compact?: boolean;
  onLayout?: ViewProps["onLayout"];
  wordCard: ReferenceWordMorphemeSlice;
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const navigate = useBrowseMorphemePageNavigator().push.mutate;
  const selectionItems = useMemo(() => [morphemeSelectionItem(wordCard.word)], [wordCard.word]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [expanded, setExpanded] = useRouteBodyState("word-surface-definitions-expanded", false);
  const pinyin = wordCard.definitionList[0]?.pinyin;
  const audioCard = {
    front: wordCard.word,
    pinyinList: pinyin === undefined ? [] : [{ toneNumbersPinyin: pinyin }],
  };
  const definitions = uniq(
    wordCard.definitionList.flatMap(({ entries }) =>
      entries.flatMap((entry) =>
        entry
          .split(";")
          .map((definition) => definition.trim())
          .filter(Boolean),
      ),
    ),
  );
  const joinedDefinitions = definitions.join("; ");
  const expandable = definitions.length > 1 || joinedDefinitions.length > 60;

  return (
    <SurfaceHeader
      className={
        compact
          ? "flex-row items-start gap-x-3 border-b-0 px-3 pb-2"
          : "flex-row items-start gap-x-2 px-0"
      }
      onLayout={onLayout}
      testID="word-dictionary-surface-header"
    >
      <View
        className={
          compact ? "max-w-[45%] shrink-0 items-center" : "max-w-[45%] shrink-0 items-center p-2"
        }
      >
        {compact ? null : (
          <PinyinPronunciation
            card={audioCard}
            pinyinList={pinyin === undefined ? [] : [toDiacriticPinyin(pinyin)]}
          />
        )}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View className={isSelected ? "flex-row rounded-xs bg-primary/10" : "flex-row"}>
            {wordCard.characters.map(({ grapheme }, index) => (
              <LongPressable
                accessibilityHint="Opens this character's info page"
                accessibilityLabel={grapheme}
                accessibilityRole="link"
                accessibilityState={{ selected: isSelected }}
                key={`${grapheme}-${index}`}
                onLongPress={onLongPress}
                onPress={() => {
                  if (!consumePress()) {
                    navigate({ morpheme: grapheme, params: {} });
                  }
                }}
              >
                <GraphemeFontGlyph
                  className="text-center text-2xl leading-8 text-foreground"
                  glyph={getGraphemeGlyph(grapheme, { uncolored: true })}
                />
              </LongPressable>
            ))}
          </View>
        </ScrollView>
        {compact ? (
          <PinyinPronunciation
            card={audioCard}
            pinyinList={pinyin === undefined ? [] : [toDiacriticPinyin(pinyin)]}
          />
        ) : null}
      </View>
      <View
        className={
          compact
            ? "min-w-0 flex-1 flex-row items-start gap-x-1"
            : "min-w-0 flex-1 flex-row items-start gap-x-1 py-2 pr-2"
        }
      >
        <View className="min-w-0 flex-1">
          {definitions.length === 0 ? (
            <Text className="text-sm text-muted-foreground italic">
              No dictionary definitions found yet.
            </Text>
          ) : expanded ? (
            <ScrollView className="max-h-36" nestedScrollEnabled>
              {definitions.map((definition, index) => (
                <Text className="text-sm leading-5 text-muted-foreground" key={index}>
                  • {definition}
                </Text>
              ))}
            </ScrollView>
          ) : (
            <Text className="text-sm leading-5 text-muted-foreground" numberOfLines={2}>
              {joinedDefinitions}
            </Text>
          )}
        </View>
        {expandable ? (
          <Pressable
            accessibilityLabel={`${expanded ? "Show fewer" : "Show all"} definitions for ${wordCard.word}`}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            className="p-1"
            hitSlop={6}
            onPress={() => setExpanded((value) => !value)}
          >
            <Icon
              className="text-muted-foreground"
              name={expanded ? "LuChevronUp" : "LuChevronDown"}
              size={16}
            />
          </Pressable>
        ) : null}
      </View>
    </SurfaceHeader>
  );
}

export function WordDictionarySurfaceBody({ wordCard }: { wordCard: ReferenceWordMorphemeSlice }) {
  const gradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(wordCard);
  return (
    <SurfaceContent
      className="flex-row items-center justify-between gap-x-2 p-3"
      testID="word-dictionary-surface-body"
    >
      {gradeLabel === null ? (
        <View />
      ) : (
        <MorphemeInfoPanelDictionaryGradeBadge label={gradeLabel} />
      )}
      <MorphemeInfoPanelDictionaryRankSection infoBackCardSlice={wordCard} />
    </SurfaceContent>
  );
}
